import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';

function AdminBookAppointment() {
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]); // Array of service objects with {id, name, price, duration}
  const [serviceToAdd, setServiceToAdd] = useState(''); // Dropdown selection
  const [totalDuration, setTotalDuration] = useState(0); // Calculated/editable duration
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedStylist, setSelectedStylist] = useState('');
  const [newClientMode, setNewClientMode] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);
  const [stylistAvailability, setStylistAvailability] = useState({});
  const [availableDates, setAvailableDates] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([]);

  useEffect(() => {
    fetchServices();
    fetchClients();
    fetchStylists();
  }, []);

  const fetchServices = async () => {
    const response = await axios.get(`${API_URL}/services`);
    setServices(response.data);
  };

  const fetchClients = async () => {
    const response = await axios.get(`${API_URL}/clients`);
    setClients(response.data);
  };

  const fetchStylists = async () => {
    try {
      const response = await axios.get(`${API_URL}/stylists`);
      setStylists(response.data);
    } catch (error) {
      console.error('Error fetching stylists:', error);
    }
  };

  // Fetch stylist availability when stylist is selected
  useEffect(() => {
    if (selectedStylist) {
      fetchStylistAvailability(selectedStylist);
    } else {
      setStylistAvailability({});
      setAvailableDates([]);
    }
  }, [selectedStylist]);

  const fetchStylistAvailability = async (stylistId) => {
    try {
      const response = await axios.get(`${API_URL}/stylists/${stylistId}/availability`);
      const availability = response.data;

      // Convert availability to a map by day of week
      const availabilityMap = {};
      availability.forEach(slot => {
        if (!availabilityMap[slot.day_of_week]) {
          availabilityMap[slot.day_of_week] = [];
        }
        availabilityMap[slot.day_of_week].push(slot);
      });
      setStylistAvailability(availabilityMap);
    } catch (error) {
      console.error('Error fetching stylist availability:', error);
    }
  };

  const handleAddService = () => {
    if (!serviceToAdd) return;

    const service = services.find(s => s.id === parseInt(serviceToAdd));
    if (!service) return;

    const newService = {
      id: service.id,
      name: service.name,
      price: service.price,
      duration: service.duration
    };

    setSelectedServices([...selectedServices, newService]);

    // Update total duration
    const newTotal = totalDuration + service.duration;
    setTotalDuration(newTotal);

    // Reset dropdown
    setServiceToAdd('');
  };

  const handleRemoveService = (index) => {
    const serviceToRemove = selectedServices[index];
    if (serviceToRemove) {
      const newServices = selectedServices.filter((_, i) => i !== index);
      setSelectedServices(newServices);
      setTotalDuration(totalDuration - serviceToRemove.duration);
    }
  };

  // Fetch time blocks when date changes
  useEffect(() => {
    if (date) {
      fetchTimeBlocks(date);
    } else {
      setTimeBlocks([]);
    }
  }, [date]);

  const fetchTimeBlocks = async (selectedDate) => {
    try {
      const response = await axios.get(`${API_URL}/time-blocks/by-date/${selectedDate}`);
      setTimeBlocks(response.data);
    } catch (error) {
      console.error('Error fetching time blocks:', error);
    }
  };

  const checkTimeBlockConflict = (appointmentTime, appointmentDuration) => {
    const [aptHour, aptMin] = appointmentTime.split(':').map(Number);
    const aptStartMinutes = aptHour * 60 + aptMin;
    const aptEndMinutes = aptStartMinutes + appointmentDuration;

    for (const block of timeBlocks) {
      // Check if block applies to all stylists or the selected stylist
      if (block.stylist_id && block.stylist_id !== parseInt(selectedStylist)) {
        continue; // This block is for a different stylist
      }

      const [blockStartHour, blockStartMin] = block.start_time.split(':').map(Number);
      const [blockEndHour, blockEndMin] = block.end_time.split(':').map(Number);
      const blockStartMinutes = blockStartHour * 60 + blockStartMin;
      const blockEndMinutes = blockEndHour * 60 + blockEndMin;

      // Check for overlap
      if (aptStartMinutes < blockEndMinutes && aptEndMinutes > blockStartMinutes) {
        return block; // Conflict found
      }
    }
    return null; // No conflict
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate at least one service is selected
    if (selectedServices.length === 0) {
      alert('Please select at least one service');
      return;
    }

    // Check for time block conflicts
    const conflict = checkTimeBlockConflict(time, totalDuration);
    if (conflict) {
      const formatTime = (time24) => {
        const [hours, minutes] = time24.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
      };

      const blockStylist = conflict.stylist_name ? `for ${conflict.stylist_name}` : 'for all stylists';
      const blockReason = conflict.reason ? ` (${conflict.reason})` : '';
      alert(
        `This time slot conflicts with a blocked time period ${blockStylist}.\n` +
        `Blocked: ${formatTime(conflict.start_time)} - ${formatTime(conflict.end_time)}${blockReason}\n\n` +
        `Please choose a different time.`
      );
      return;
    }

    try {
      let clientId = selectedClient;

      // Create new client if in new client mode
      if (newClientMode) {
        // Check if client with this phone number already exists
        const existingClient = clients.find(
          client => client.phone.replace(/\D/g, '') === newClientPhone.replace(/\D/g, '')
        );

        if (existingClient) {
          // Use existing client but update their info
          clientId = existingClient.id;
          await axios.put(`${API_URL}/clients/${clientId}`, {
            name: newClientName,
            email: newClientEmail || existingClient.email,
            phone: newClientPhone,
            notes: existingClient.notes
          });
          alert(`Client already exists! Updated ${existingClient.name}'s information.`);
        } else {
          // Create new client
          const clientResponse = await axios.post(`${API_URL}/clients`, {
            name: newClientName,
            phone: newClientPhone,
            email: newClientEmail,
            notes: ''
          });
          clientId = clientResponse.data.id;
        }
      }

      await axios.post(`${API_URL}/appointments`, {
        client_id: clientId,
        service_ids: selectedServices.map(s => s.id), // Send array of service IDs only
        appointment_date: date,
        appointment_time: time,
        notes,
        stylist_id: selectedStylist || null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        // Reset form
        setSelectedServices([]);
        setTotalDuration(0);
        setServiceToAdd('');
        setSelectedClient('');
        setSelectedStylist('');
        setNewClientMode(false);
        setNewClientName('');
        setNewClientPhone('');
        setNewClientEmail('');
        setDate('');
        setTime('');
        setNotes('');
      }, 2000);

      // Refresh clients list if new client was added
      if (newClientMode) {
        fetchClients();
      }
    } catch (err) {
      alert('Failed to book appointment: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="admin-book-appointment">
      <h2>Book Appointment</h2>

      {success ? (
        <div className="success-banner">
          <h3>✓ Appointment Booked Successfully!</h3>
        </div>
      ) : (
        <div className="booking-form-container">
          <form onSubmit={handleSubmit} className="admin-booking-form">

            <div className="form-section">
              <h3>Services</h3>
              <div className="form-group">
                <label>Add Service *</label>
                <div className="service-add-row">
                  <select
                    value={serviceToAdd}
                    onChange={(e) => setServiceToAdd(e.target.value)}
                    className="service-dropdown"
                  >
                    <option value="">Choose a service...</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name} - ${service.price} ({service.duration} min)
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddService}
                    className="btn-add-service"
                    disabled={!serviceToAdd}
                  >
                    Add Service
                  </button>
                </div>

                {selectedServices.length > 0 && (
                  <div className="selected-services-list">
                    <h4>Selected Services:</h4>
                    {selectedServices.map((service, index) => (
                      <div key={index} className="selected-service-item">
                        <span className="service-name">{service.name}</span>
                        <span className="service-details">${service.price} • {service.duration} min</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveService(index)}
                          className="btn-remove-service"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    <div className="services-summary-box">
                      <div className="summary-row">
                        <span>Total Services:</span>
                        <strong>{selectedServices.length}</strong>
                      </div>
                      <div className="summary-row">
                        <span>Total Price:</span>
                        <strong>${selectedServices.reduce((sum, s) => sum + parseFloat(s.price), 0).toFixed(2)}</strong>
                      </div>
                      <div className="summary-row">
                        <span>Total Duration:</span>
                        <input
                          type="number"
                          value={totalDuration}
                          onChange={(e) => setTotalDuration(parseInt(e.target.value) || 0)}
                          className="duration-input"
                          min="0"
                        />
                        <span>minutes</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3>Client</h3>
              <div className="client-mode-toggle">
                <button
                  type="button"
                  className={!newClientMode ? 'active' : ''}
                  onClick={() => setNewClientMode(false)}
                >
                  Existing Client
                </button>
                <button
                  type="button"
                  className={newClientMode ? 'active' : ''}
                  onClick={() => setNewClientMode(true)}
                >
                  New Client
                </button>
              </div>

              {!newClientMode ? (
                <div className="form-group">
                  <label>Select Client *</label>
                  <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} required>
                    <option value="">Choose a client...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} - {client.phone}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Client Name *</label>
                    <input
                      type="text"
                      value={newClientName}
                      onChange={(e) => setNewClientName(e.target.value)}
                      placeholder="Enter client name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      value={newClientPhone}
                      onChange={(e) => setNewClientPhone(e.target.value)}
                      placeholder="(555) 555-5555"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email (Optional)</label>
                    <input
                      type="email"
                      value={newClientEmail}
                      onChange={(e) => setNewClientEmail(e.target.value)}
                      placeholder="client@email.com"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="form-section">
              <h3>Stylist</h3>
              <div className="form-group">
                <label>Select Stylist *</label>
                <select value={selectedStylist} onChange={(e) => setSelectedStylist(e.target.value)} required>
                  <option value="">Choose a stylist...</option>
                  {stylists.map(stylist => (
                    <option key={stylist.id} value={stylist.id}>
                      {stylist.name}
                    </option>
                  ))}
                </select>
                {selectedStylist && Object.keys(stylistAvailability).length > 0 && (
                  <small style={{display: 'block', marginTop: '8px', color: '#666'}}>
                    This stylist has limited availability. Please select a date/time within their working hours.
                  </small>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3>Appointment Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Time *</label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requests or notes..."
                  rows="3"
                />
              </div>
            </div>

            <button type="submit" className="submit-btn">Book Appointment</button>
          </form>
        </div>
      )}
    </div>
  );
}

export default AdminBookAppointment;
